import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils/formatters";
import { PropsWithChildren } from "react";

type CriterionDetails = Record<string, unknown> | undefined;

const CriterionDetailsBadge = ({ children }: PropsWithChildren) => (
  <Badge variant="outline" className="text-sm text-muted-foreground">
    {children}
  </Badge>
);

export const CriterionDetails = ({
  details,
}: {
  details: CriterionDetails;
}) => {
  const { t } = useTranslation();

  if (!details) return null;

  const parts: JSX.Element[] = [];

  for (const [key, value] of Object.entries(details)) {
    if (value === null || value === undefined) {
      continue;
    }

    // Special case that should not be rendered
    if (key === "has_changes") {
      continue;
    }

    const translationKey = `projectPeriodConfirmation.details.${key}`;
    const safeValue = String(value);

    switch (key) {
      case "utilization_percent":
        parts.push(
          <CriterionDetailsBadge key={key}>{safeValue}%</CriterionDetailsBadge>,
        );
        break;
      case "project_end_date":
      case "minimum_required_end_date":
        parts.push(
          <span key={key}>
            {t(translationKey)}{" "}
            <CriterionDetailsBadge>
              {formatDate(safeValue)}
            </CriterionDetailsBadge>
          </span>,
        );
        break;
      case "changes": {
        const changes = value as {
          field: string;
          old_value: string;
          new_value: string;
        }[];
        parts.push(
          <span key={key}>
            {changes.length === 0 && (
              <strong className="font-bold mr-3">{t("common.no")}</strong>
            )}
            {changes.map((change, i) => (
              <div key={i}>
                {t(`clients.fields.${change.field}`)}:{" "}
                <strong>
                  {change.old_value} → {change.new_value}
                </strong>
              </div>
            ))}
          </span>,
        );
        break;
      }
      case "status": {
        const statusKey = String(value);
        const statusLabel = t(`projectStatuses.${statusKey}`, statusKey);
        parts.push(
          <CriterionDetailsBadge key={key}>
            {statusLabel}
          </CriterionDetailsBadge>,
        );
        break;
      }
      default:
        parts.push(
          <span key={key}>
            {t(translationKey)}{" "}
            <CriterionDetailsBadge>{safeValue}</CriterionDetailsBadge>
          </span>,
        );
        break;
    }
  }

  if (parts.length === 0) return null;

  return <span className="flex flex-wrap gap-y-1 gap-x-3">{parts}</span>;
};
